import type { CardImageDetailDto, CreateAnnotationRequest } from '@fiszki/shared-contracts';
import { deleteJson, fetchJson, patchJson, postJson } from '../client';

export interface CardResponse {
  id: number;
  deckId: number;
  type: 'basic' | 'cloze' | 'image' | 'imageText';
  frontContent: string | null;
  backContent: string | null;
  clozeCount: number | null;
  position: number;
  flagReverse: boolean;
  /** Cloze-only — tryb wpisywania odpowiedzi w sesji nauki. Zawsze false dla basic/image. */
  requireTyping: boolean;
  variants: string[];
  createdAt: string;
  updatedAt: string;
  /**
   * Dla type='image' — non-null revealLabels adnotacji (pinpoint + occlusion z labelem)
   * w Position ASC. Dla basic/cloze — pusta lista. Backend joinuje CardAnnotations × Cards
   * w ListCardsByDeckFunction (single roundtrip). Frontend dokleja do search haystack
   * w deck editor (filter szuka też po odpowiedziach na obrazie).
   */
  annotationLabels: string[];
  /** Indywidualne wyróżnienie ("gwiazdka") karty przez bieżącego usera. */
  isStarred?: boolean;
  /**
   * ImageText-only orientacja: true → przód=zdjęcie, tył=tekst (BackContent); false → przód=tekst,
   * tył=zdjęcie. Logicznie nieużywane dla basic/cloze/image. **Wire reality:** OPCJONALNE (backend
   * `bool` default-valued; generated mirror `imageOnFront?: boolean`). Edytor init przełącznika
   * orientacji w edit mode czyta to pole (traktuj `undefined` jak `true` per backend default).
   */
  imageOnFront?: boolean;
}

export interface CardListResponse {
  items: CardResponse[];
}

/**
 * Faza 3 (T23) — type union extended z 'image' per spec §9.1.
 * Image-only fields (`imageId`, `annotations`) MUSZĄ być undefined dla type='basic'|'cloze'
 * (Fix 11 regression: backend rejection przy mismatched payload). UI w `card-editor.tsx`
 * dispatcher per-type guarantees this isolation.
 */
export interface CreateCardPayload {
  type: 'basic' | 'cloze' | 'image' | 'imageText';
  frontContent?: string;
  backContent?: string;
  flagReverse?: boolean;
  position?: number;
  imageId?: number;
  annotations?: CreateAnnotationRequest[];
  /** Cloze-only — tryb wpisywania. Backend 400 `typing_requires_cloze` jeśli true dla nie-cloze. */
  requireTyping?: boolean;
  /**
   * ImageText-only orientacja: true → przód=zdjęcie, tył=tekst; false → przód=tekst, tył=zdjęcie.
   * Backend default true gdy pominięte. `PatchCardPayload` dziedziczy to jako opcjonalne.
   */
  imageOnFront?: boolean;
}

export type PatchCardPayload = Partial<Omit<CreateCardPayload, 'type'>>;

export const cardsApi = {
  listByDeck: (deckId: number) => fetchJson<CardListResponse>(`/decks/${deckId}/cards`),
  create: (deckId: number, payload: CreateCardPayload) =>
    postJson<CardResponse, CreateCardPayload>(`/decks/${deckId}/cards`, payload),
  patch: (id: number, payload: PatchCardPayload) =>
    patchJson<CardResponse, PatchCardPayload>(`/cards/${id}`, payload),
  delete: (id: number) => deleteJson<void>(`/cards/${id}`),
  getImageDetail: (cardId: number) =>
    fetchJson<CardImageDetailDto>(`/cards/${cardId}/image-detail`),
  /** Ustawia/zdejmuje indywidualną gwiazdkę karty (per user). Idempotentne. */
  setStar: (cardId: number, starred: boolean) =>
    postJson<void, { starred: boolean }>(`/cards/${cardId}/star`, { starred }),
};
